/**
 * useReportTemplates Hook
 * Fetches and caches report templates (Level 1 of 3-level architecture)
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { reportTemplateAPI } from '../api.js';

// Category display configuration
export const CATEGORY_CONFIG = {
  conformite: {
    label: 'Conformité',
    color: 'blue',
    icon: 'ClipboardCheck'
  },
  inventaire: {
    label: 'Inventaire Flotte',
    color: 'purple',
    icon: 'Car'
  },
  couts: {
    label: 'Coûts & Budget',
    color: 'amber',
    icon: 'Calculator'
  },
  risques: {
    label: 'Risques Opérationnels',
    color: 'red',
    icon: 'AlertTriangle'
  },
  mensuel: {
    label: 'Rapports Mensuels',
    color: 'green',
    icon: 'Calendar'
  },
  assurance: {
    label: 'Exports Assurance',
    color: 'indigo',
    icon: 'Shield'
  }
};

export function useReportTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await reportTemplateAPI.list();
      if (response.ok) {
        setTemplates(response.data || []);
      } else {
        throw new Error(response.error?.message || 'Failed to load templates');
      }
    } catch (err) {
      console.error('Error loading report templates:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Group templates by category
  const byCategory = useMemo(() => {
    const groups = {};
    templates.forEach(template => {
      const cat = template.category || 'other';
      if (!groups[cat]) {
        groups[cat] = {
          key: cat,
          ...CATEGORY_CONFIG[cat] || { label: 'Autres', color: 'gray', icon: 'File' },
          templates: []
        };
      }
      groups[cat].templates.push(template);
    });
    return groups;
  }, [templates]);

  // Get template by code
  const getByCode = useCallback((code) => {
    return templates.find(t => t.code === code);
  }, [templates]);

  // Get template by ID
  const getById = useCallback((id) => {
    return templates.find(t => t.id === id);
  }, [templates]);

  return {
    templates,
    byCategory,
    loading,
    error,
    reload: loadTemplates,
    getByCode,
    getById
  };
}

export default useReportTemplates;
